import React, { useContext, useState, useEffect } from 'react';
import useGetFeatureAccess from 'hooks/useGetUserAuthData';
import {
    AccessLevels, IFeatureAccess, IFeatureAccessMap, TAccessLevel, TFeatureId, TUserClassId, IRole,
} from 'types/auth';
import useGetAuthenticatedUser, { User } from 'queries/UserMenu/useGetAuthenticatedUser';
import { USER_CLASSES, SELECTED_ORGANIZATION_ID } from 'services/constants';
import useGetUserOrganizations, { IUserOrganization } from 'hooks/useGetUserOrganizations';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';
import { getCookie, setCookie } from 'helpers/localStorage/cookies';

export interface IAuthContext {
    selectedOrganizationId?: string;
    setSelectedOrganizationId: (orgId: string|undefined) => void;
    userOrganizations: IUserOrganization[];
    isLoadingUserOrganizations: boolean;
    orgPermissions: IFeatureAccessMap;
    isLoadingAuthData: boolean;
    isFeatureAccessibleToUser: (featureIds: TFeatureId[], requiredAccessLevel: TAccessLevel) => boolean;
    role: IRole | null;
    user: User | null;
}

const defaultAuthContext: IAuthContext = {
    selectedOrganizationId: undefined,
    setSelectedOrganizationId: () => {},
    userOrganizations: [],
    isLoadingUserOrganizations: true,
    orgPermissions: {},
    isLoadingAuthData: true,
    isFeatureAccessibleToUser: () => false,
    role: null,
    user: null,
};

export const AuthContext = React.createContext<IAuthContext>(defaultAuthContext);

export const useAuthContext = () => useContext(AuthContext);
export default useAuthContext;

export const featureArrayToObject = (features: IFeatureAccess[] | undefined): IFeatureAccessMap => {
    if (!features) return {};
    return features.reduce((featuresObj, feature) => ({
        ...featuresObj,
        [feature.id]: feature,
    }), {} as IFeatureAccessMap);
};

export const isFeatureAccessible = (
    featureIds: number[],
    requiredAccessLevel: TAccessLevel,
    orgPermissions: IFeatureAccessMap,
    userClass: TUserClassId | undefined,
) => {
    if (userClass === USER_CLASSES.SUPER_ADMIN) return true;
    return featureIds.some(featureId => {
        const accessLevel = orgPermissions?.[featureId]?.accessLevel ?? AccessLevels.NO_ACCESS;
        return accessLevel >= requiredAccessLevel;
    });
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const { data: authenticatedUser, isLoading: isLoadingAuthenticatedUser } = useGetAuthenticatedUser();

    const getUserSpecificOrgKey = (userId: number): string => `${SELECTED_ORGANIZATION_ID}_${userId}`;

    const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | undefined>(undefined);

    // Load organization selection from user-specific cookie when user changes
    useEffect(() => {
        if (authenticatedUser?.user?.id) {
            const userSpecificSelectedOrgKey = getUserSpecificOrgKey(authenticatedUser.user.id);
            const userSpecificSelectedOrgCookie = getCookie(userSpecificSelectedOrgKey);

            if (userSpecificSelectedOrgCookie && userSpecificSelectedOrgCookie !== selectedOrganizationId) {
                setSelectedOrganizationId(userSpecificSelectedOrgCookie);
            }
        }
    }, [authenticatedUser?.user?.id]);

    useEffect(() => {
        if (selectedOrganizationId && authenticatedUser?.user?.id) {
            const userSpecificSelectedOrgKey = getUserSpecificOrgKey(authenticatedUser.user.id);
            setCookie(userSpecificSelectedOrgKey, selectedOrganizationId, 'path=/; SameSite=None; Secure; Max-Age=34560000');
        }
    }, [selectedOrganizationId, authenticatedUser?.user?.id]);

    const { data: userAuthData, isLoading: isLoadingUserAuthData } = useGetFeatureAccess(selectedOrganizationId!, {
        enabled: Boolean(selectedOrganizationId),
        staleTime: Infinity,
    });

    const orgPermissions = featureArrayToObject(userAuthData?.featureAccess) || {};

    const { setIdentity } = useTrackingCode();

    const isFeatureAccessibleToUser = (
        featureIds: TFeatureId[], requiredAccessLevel: TAccessLevel,
    ) => isFeatureAccessible(featureIds, requiredAccessLevel, orgPermissions, authenticatedUser?.user.userClass);

    const { data: userOrganizations = [], isLoading: isLoadingUserOrganizations } = useGetUserOrganizations();

    useEffect(() => {
        if (!selectedOrganizationId && userOrganizations.length > 0) {
            setSelectedOrganizationId(userOrganizations[0].organization.correlationId);
        }
    }, [userOrganizations, selectedOrganizationId]);

    useEffect(() => {
        if (
            !isLoadingUserOrganizations
            && !isLoadingAuthenticatedUser
            && authenticatedUser
        ) {
            setIdentity(authenticatedUser.user.email, { id: authenticatedUser.user.id });
            if ((window as any).heap) {
                (window as any).heap.identify(authenticatedUser.user.email);
                (window as any).heap.addUserProperties({
                    email: authenticatedUser.user.email,
                    name: authenticatedUser.user.name,
                    user_class: authenticatedUser.user.userClass,
                    organization_id: userOrganizations.map(org => org.organization.id).join(', '),
                    organization_name: userOrganizations.map(org => org.organization.name).join(', '),
                });
            }
        }
    }, [authenticatedUser, userOrganizations, isLoadingUserOrganizations, isLoadingAuthenticatedUser]);

    return (
        <AuthContext.Provider
            value={{
                selectedOrganizationId: selectedOrganizationId || undefined,
                setSelectedOrganizationId,
                userOrganizations,
                isLoadingUserOrganizations,
                orgPermissions,
                isLoadingAuthData: isLoadingUserAuthData || isLoadingAuthenticatedUser,
                isFeatureAccessibleToUser,
                role: userAuthData?.role || null,
                user: authenticatedUser?.user || null,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
